import { CSSProperties, ElementType, MouseEventHandler, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@mui/material';
import AgTypography from '../../Styles/AgTypography';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import { AgColor } from '../../Styles/AgColor';

export type AgButtonProps = {
  dataTestId?: string,
  loading?: boolean,
  loadingMessage?: string,
  disabled?: boolean,
  startIconSample?: boolean, //TODO for storybook demonstration purpose only
  endIconSample?: boolean, //TODO for storybook demonstration purpose only
  startIcon?: ReactNode,
  endIcon?: ReactNode,
  parentStyle?: CSSProperties | undefined,
  style?: CSSProperties | undefined,
  onClick?: MouseEventHandler<HTMLButtonElement>,
  label: string,
  labelSuffix?: string,
  width?: string,
  component?: ElementType;
  variant?: 'text' | 'outlined' | 'contained' | undefined,
  color?: 'primary' | 'secondary' | 'error' | undefined,
  type?: 'submit' | 'reset' | 'button',
  small?: boolean,
  textTransform?: 'capitalize' | 'full-size-kana' | 'full-width' | 'lowercase' | 'none' | 'uppercase',
};

const AgButton = (
  {
    loading,
    loadingMessage = 'LOADING',
    disabled,
    startIcon,
    endIcon,
    startIconSample,
    endIconSample,
    parentStyle,
    style,
    onClick, //required
    label, //required
    labelSuffix,
    width,
    variant = 'contained',
    color = 'primary',
    type = 'button',
    small = false,
    component,
    textTransform,
    dataTestId
  }: AgButtonProps) => {
  const { formatMessage } = useIntl();

  const colorMapping = {
    primary: {
      contained: {
        bgHoverColor: AgColor.secondaryMain,
        hoverFontColor: AgColor.grey0
      },
      outlined: {
        bgHoverColor: AgColor.secondaryLight,
        hoverFontColor: AgColor.secondaryDark,
        hoverBorder: `1px solid ${AgColor.grey0}`
      },
      text: {
        bgHoverColor: AgColor.grey0,
        hoverFontColor: AgColor.secondaryMain
      }
    },
    secondary: {
      contained: {
        bgHoverColor: AgColor.primaryLight,
        hoverFontColor: AgColor.grey0
      },
      outlined: {
        bgHoverColor: AgColor.primaryLight2,
        hoverFontColor: AgColor.primaryDark,
        hoverBorder: `1px solid ${AgColor.grey0}`
      },
      text: {
        bgHoverColor: AgColor.grey0,
        hoverFontColor: AgColor.primaryLight
      }
    },
    error: {
      contained: {
        bgHoverColor: AgColor.errorHighlight,
        hoverFontColor: AgColor.grey0
      },
      outlined: {
        bgHoverColor: AgColor.errorLight,
        hoverFontColor: AgColor.errorMain,
        hoverBorder: `1px solid ${AgColor.grey0}`
      },
      text: {
        bgHoverColor: AgColor.grey0,
        hoverFontColor: AgColor.errorHighlight
      }
    }
  };

  const {
    bgHoverColor,
    hoverFontColor,
    hoverBorder
  } = colorMapping[color]?.[variant] as {
    bgHoverColor: string;
    hoverFontColor: string;
    hoverBorder?: string;
  } || {};

  return (
    <AgTypography
      onClick={disabled ? ()=>{} : onClick}
      component={component}
      style={{
        padding: '6px 0',
        cursor: disabled ? 'default' : 'pointer',
        width: width ?? 'fit-content',
        ...parentStyle
      }}>
      <Button
        data-testid={dataTestId}
        disabled={disabled || loading}
        startIcon={startIconSample ? <FolderCopyOutlinedIcon sx={{ height: '16px' }} /> : startIcon}
        endIcon={endIconSample ? <FolderCopyOutlinedIcon sx={{ height: '16px' }} /> : endIcon}
        variant={variant}
        color={color}
        type={type}
        style={{
          fontFamily: 'Roboto, Sans-Serif',
          fontWeight: 700,
          lineHeight: '16px',
          fontSize: '12px',
          letterSpacing: '4%',
          textTransform: textTransform ?? 'uppercase',
          padding: small ? '2px 16px' : '10px 24px',
          boxShadow: 'none',
          width: 'inherit',
          ...(disabled && { backgroundColor: AgColor.grey5 }),
          ...style
        }}
        sx={{
          ':hover': {
            bgcolor: bgHoverColor,
            color: hoverFontColor,
            border: hoverBorder
          }
        }}
      >
        {((!!loading) && (formatMessage({ id: (loadingMessage || 'LOADING') })))}
        {(!loading) && (<>{formatMessage({ id: label })[(textTransform !== 'none') ? 'toLowerCase' : 'toString']()}{labelSuffix}</>)}
      </Button>
    </AgTypography>
  );
};

export default AgButton;